import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  ENQUIRY_STAGES, ENQUIRY_STAGES_BY_KEY,
  PROJECT_STAGES, PROJECT_STAGES_BY_KEY,
  SUB_STATUSES_BY_KEY,
} from "@/lib/workflow";
import {
  Inbox, FileText, Wrench, AlertTriangle, Clock, TrendingUp, ChevronRight,
  Upload, ArrowRight,
} from "lucide-react";

/**
 * Unified dashboard — pulls from GET /api/dashboard/summary.
 * Every KPI card drills into a filtered list (enquiries / quotes / projects).
 * Data visibility is scoped server-side by role.
 */
export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/dashboard/summary");
        setData(d);
      } catch (e) { setError(formatApiError(e)); }
    })();
  }, []);

  if (error) return <DesktopLayout><div className="text-red-600">{error}</div></DesktopLayout>;
  if (!data) return <DesktopLayout><div className="text-slate-500">Loading dashboard…</div></DesktopLayout>;

  const enq = data.enquiries || {};
  const q = data.quotes || {};
  const p = data.projects || {};
  const mine = data.my_pending || { enquiries: [], projects: [], total: 0 };
  const scopeNote = data.scope === "own" ? "Your assigned items only" : "All company data";
  // Production Design sees ONLY the project pipeline — hide all enquiry / quote
  // KPIs and panels for that role (they do not touch pre-quote work).
  const projectOnly = user?.role === "production_design";
  // 2D/3D Design gets a minimal task-queue dashboard — no KPIs, pipelines, or
  // status panels. Just "Enquiries waiting on you for drawings" and a jump to
  // their enquiry list.
  if (user?.role === "design") {
    return <DesignTaskQueue user={user} mine={mine} data={data} />;
  }
  // Production & Installation now get a dedicated board with Pending + Upcoming
  if (user?.role === "production" || user?.role === "installation") {
    return <ProductionBoardDashboard user={user} />;
  }

  return (
    <DesktopLayout>
      <div className="mb-6">
        <div className="hk-tiny-label" style={{ color: "#E52B50" }}>Dashboard</div>
        <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>
          Hey {user?.name?.split(" ")[0] || "there"},
        </h1>
        <p className="text-sm text-slate-600 mt-1">Live view of your pipeline · <span className="font-semibold">{scopeNote}</span></p>
      </div>

      {/* Pending Tasks table — every user sees their task queue in one line */}
      <PendingTasksTable rows={mine.projects || []} />

      {/* Top KPI row */}
      <div className={`grid gap-4 mb-6 ${projectOnly ? "sm:grid-cols-1" : "sm:grid-cols-2 lg:grid-cols-4"}`}>
        {!projectOnly && (
          <KpiCard title="Open Enquiries" value={enq.open || 0} icon={Inbox}
            accent="#E52B50" to="/enquiries" testId="kpi-enquiries-open"
            subtitle={`${enq.total || 0} total`} />
        )}
        {!projectOnly && (
          <KpiCard title="Active Quotes" value={q.total || 0} icon={FileText}
            accent="#7C3AED" to="/quotes" testId="kpi-quotes"
            subtitle={`₹${(q.value || 0).toLocaleString()} value`} />
        )}
        <KpiCard title="Active Projects" value={p.active || 0} icon={Wrench}
          accent="#F97316" to="/projects" testId="kpi-projects-active"
          subtitle={`${p.total || 0} total`} />
        {!projectOnly && (
          <KpiCard title="Converted (Quotes → Projects)" value={q.converted || 0} icon={TrendingUp}
            accent="#059669" to="/projects" testId="kpi-conversions" />
        )}
      </div>

      {/* Blocked / overdue alerts — bottom of the page per user request */}
      {/* Placeholder removed; moved to end. */}

      {/* Enquiry pipeline — hidden for production_design. 10 stages
        distributed as 5+5 for a cleaner two-row grid. */}
      {!projectOnly && (
        <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-6" data-testid="enquiry-pipeline-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[11px] font-black uppercase tracking-widest text-slate-700 flex items-center gap-2">
              <span className="inline-block w-1 h-4 rounded-sm bg-gradient-to-b from-rose-500 to-orange-500" />
              Enquiry pipeline
            </h2>
            <Link to="/enquiries"
              className="text-[11px] font-bold uppercase tracking-wide bg-orange-50 hover:bg-orange-100 text-orange-700 px-3 py-1.5 rounded-full transition-colors">
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {ENQUIRY_STAGES.filter((s) => !["quoted"].includes(s.key)).map((s) => (
              <StageTile key={s.key} label={s.label} count={enq.by_stage?.[s.key] || 0}
                color={s.color} bg={s.bg} to={`/enquiries?stage=${s.key}`}
                testId={`enq-tile-${s.key}`} />
            ))}
          </div>
        </section>
      )}

      {/* Project pipeline — 9 stage tiles + a "Open issues" tile = 10 total,
          laid out 5+5. Clicking Issues takes you to the projects list
          filtered to items with an open issue. */}
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-6" data-testid="project-pipeline-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[11px] font-black uppercase tracking-widest text-slate-700 flex items-center gap-2">
            <span className="inline-block w-1 h-4 rounded-sm bg-gradient-to-b from-sky-500 to-violet-500" />
            Project pipeline
          </h2>
          <Link to="/projects"
            className="text-[11px] font-bold uppercase tracking-wide bg-orange-50 hover:bg-orange-100 text-orange-700 px-3 py-1.5 rounded-full transition-colors">
            View all →
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {PROJECT_STAGES.filter((s) => s.key !== "customer_approval").map((s) => (
            <StageTile key={s.key} label={s.label} count={p.by_stage?.[s.key] || 0}
              color={s.color} bg={s.bg} to={`/projects?stage=${s.key}`}
              testId={`proj-tile-${s.key}`} />
          ))}
          <StageTile label="Open Issues" count={p.open_issues || 0}
            color="#b91c1c" bg="#fee2e2" to="/projects?has_open_issue=1"
            testId="proj-tile-issues" />
        </div>
      </section>

      {/* Sub-status distribution */}
      <section className={`grid gap-6 mb-6 ${projectOnly ? "md:grid-cols-1" : "md:grid-cols-2"}`}>
        {!projectOnly && (
          <SubStatusPanel title="Enquiry statuses" byKey={enq.by_sub_status || {}} testId="enq-substatus" />
        )}
        <SubStatusPanel title="Project statuses" byKey={p.by_sub_status || {}} testId="proj-substatus" />
      </section>

      {/* Recent activity */}
      <section className={`grid gap-6 mb-6 ${projectOnly ? "md:grid-cols-1" : "md:grid-cols-2"}`}>
        {!projectOnly && <RecentEnquiries items={enq.recent || []} />}
        <RecentProjects items={p.recent || []} />
      </section>

      {/* Needs attention — moved to the bottom per user request. Only rendered
        when there's something actionable so we don't leave a blank alert card. */}
      {(enq.blocked?.length || p.blocked?.length || p.overdue?.length) ? (
        <section className="hk-card p-5 mb-10 border-l-4 border-l-red-500" data-testid="alerts-card">
          <h2 className="hk-tiny-label mb-3 flex items-center gap-1 text-red-700">
            <AlertTriangle className="w-3 h-3" /> Needs attention
          </h2>
          <div className={`grid gap-4 text-sm ${projectOnly ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
            {!projectOnly && (
              <BlockList title="Blocked enquiries" items={enq.blocked || []} linkBase="/enquiries" idField="id" labelField="name" testId="blocked-enquiries" emptyMsg="None blocked" />
            )}
            <BlockList title="Blocked projects" items={p.blocked || []} linkBase="/projects" idField="id" labelField="name" testId="blocked-projects" emptyMsg="None blocked" />
            <OverdueList title="Overdue projects" items={p.overdue || []} testId="overdue-projects" />
          </div>
        </section>
      ) : null}
    </DesktopLayout>
  );
}

function KpiCard({ title, value, subtitle, icon: Icon, accent, to, testId }) {
  // Iter80 redesign — vibrant, premium card style:
  //   • soft gradient wash tinted by the accent
  //   • coloured left-border stripe
  //   • soft drop shadow that lifts on hover
  //   • icon in a rounded coloured badge (not a plain square)
  //   • bigger, bolder number; muted uppercase label
  const wash = `linear-gradient(135deg, ${accent}0d 0%, ${accent}03 55%, #ffffff 100%)`;
  const Card = ({ children }) => to
    ? <Link to={to} data-testid={testId}
        style={{ background: wash, borderLeftColor: accent }}
        className="block border border-slate-200 border-l-4 rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-5">
        {children}
      </Link>
    : <div data-testid={testId}
        style={{ background: wash, borderLeftColor: accent }}
        className="border border-slate-200 border-l-4 rounded-xl shadow-sm p-5">
        {children}
      </div>;
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{title}</div>
          <div className="text-[42px] leading-none font-black mt-2 tracking-tight"
               style={{ fontFamily: "Chivo", color: accent }}>{value}</div>
          {subtitle && <div className="text-xs text-slate-500 mt-2 font-medium">{subtitle}</div>}
        </div>
        <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
             style={{ backgroundColor: `${accent}1a`, boxShadow: `0 0 0 4px ${accent}0d` }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
      </div>
    </Card>
  );
}

function StageTile({ label, count, color, bg, to, testId }) {
  // Iter80 — matching pipeline tile treatment: coloured left border, soft
  // background wash, chunkier number, saturated pill label.
  const wash = `linear-gradient(135deg, ${color}0a 0%, #ffffff 70%)`;
  return (
    <Link to={to} data-testid={testId}
      style={{ background: wash, borderLeftColor: color }}
      className="rounded-xl border border-slate-200 border-l-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-3 group block">
      <div className="text-[10px] font-black uppercase leading-tight mb-2 tracking-wide truncate"
           style={{ color, backgroundColor: bg, padding: "3px 8px",
                     display: "inline-block", borderRadius: 999 }}>
        {label}
      </div>
      <div className="flex items-baseline justify-between">
        <span className="text-3xl font-black tracking-tight" style={{ fontFamily: "Chivo", color }}>{count}</span>
        <ChevronRight className="w-3 h-3 text-slate-300 group-hover:text-slate-600 transition-colors" />
      </div>
    </Link>
  );
}

function SubStatusPanel({ title, byKey, testId }) {
  const entries = Object.entries(byKey);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow" data-testid={testId}>
      <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-700 mb-3 flex items-center gap-2">
        <span className="inline-block w-1 h-4 rounded-sm bg-gradient-to-b from-emerald-500 to-sky-500" />
        {title}
      </h3>
      {total === 0 ? (
        <div className="text-sm text-slate-500">No records.</div>
      ) : (
        <div className="space-y-2.5">
          {entries.map(([key, count]) => {
            const meta = SUB_STATUSES_BY_KEY[key] || { label: key, color: "#0f172a", bg: "#e2e8f0" };
            const pct = total ? Math.round((count / total) * 100) : 0;
            return (
              <div key={key}>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="font-bold px-2 py-0.5 rounded-full"
                        style={{ color: meta.color, backgroundColor: meta.bg }}>
                    {meta.label}
                  </span>
                  <span className="text-slate-500 font-semibold">{count} · {pct}%</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all"
                       style={{ width: `${pct}%`,
                                 background: `linear-gradient(90deg, ${meta.color}, ${meta.color}bb)` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BlockList({ title, items, linkBase, idField, labelField, testId, emptyMsg }) {
  return (
    <div>
      <div className="hk-tiny-label mb-2">{title}</div>
      {(items || []).length === 0 ? (
        <div className="text-xs text-slate-400" data-testid={`${testId}-empty`}>{emptyMsg}</div>
      ) : (
        <ul className="space-y-1.5">
          {items.slice(0, 5).map((it) => (
            <li key={it[idField]} className="text-xs" data-testid={`${testId}-item`}>
              <Link to={`${linkBase}/${it[idField]}`} className="block bg-red-50 border border-red-200 rounded-sm p-2 hover:bg-red-100">
                <div className="font-bold text-slate-900">{it[labelField] || it.project_id || it.enquiry_id}</div>
                <div className="text-slate-500 mt-0.5 truncate">{it.reason || it.sub_status?.replace(/_/g, " ")}</div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OverdueList({ title, items, testId }) {
  return (
    <div>
      <div className="hk-tiny-label mb-2">{title}</div>
      {(items || []).length === 0 ? (
        <div className="text-xs text-slate-400" data-testid={`${testId}-empty`}>None overdue</div>
      ) : (
        <ul className="space-y-1.5">
          {items.slice(0, 5).map((it) => (
            <li key={it.id} className="text-xs" data-testid={`${testId}-item`}>
              <Link to={`/projects/${it.id}`} className="block bg-amber-50 border border-amber-200 rounded-sm p-2 hover:bg-amber-100">
                <div className="font-bold text-slate-900 truncate">{it.name}</div>
                <div className="text-slate-500 mt-0.5 flex items-center gap-1"><Clock className="w-3 h-3" /> {it.estimated_delivery_date}</div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RecentEnquiries({ items }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow" data-testid="recent-enquiries-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-700 flex items-center gap-2">
          <span className="inline-block w-1 h-4 rounded-sm bg-gradient-to-b from-orange-500 to-rose-500" />
          Recent enquiries
        </h3>
        <Link to="/enquiries"
          className="text-[11px] font-bold uppercase tracking-wide bg-orange-50 hover:bg-orange-100 text-orange-700 px-3 py-1.5 rounded-full transition-colors">
          View all →
        </Link>
      </div>
      {(items || []).length === 0 ? (
        <div className="text-sm text-slate-500">Nothing recent.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((e) => {
            const badge = ENQUIRY_STAGES_BY_KEY[e.stage] || {};
            return (
              <li key={e.id} className="py-2.5">
                <Link to={`/enquiries/${e.id}`} className="flex items-center justify-between gap-3 hover:text-orange-600">
                  <div className="min-w-0">
                    <div className="font-bold text-sm truncate">{e.name}</div>
                    <div className="text-xs text-slate-500 truncate">{e.phone} · {(e.requirements || []).slice(0, 2).join(", ")}</div>
                  </div>
                  <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full whitespace-nowrap shrink-0"
                    style={{ color: badge.color, backgroundColor: badge.bg }}>{badge.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function RecentProjects({ items }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow" data-testid="recent-projects-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-700 flex items-center gap-2">
          <span className="inline-block w-1 h-4 rounded-sm bg-gradient-to-b from-sky-500 to-indigo-500" />
          Recent projects
        </h3>
        <Link to="/projects"
          className="text-[11px] font-bold uppercase tracking-wide bg-orange-50 hover:bg-orange-100 text-orange-700 px-3 py-1.5 rounded-full transition-colors">
          View all →
        </Link>
      </div>
      {(items || []).length === 0 ? (
        <div className="text-sm text-slate-500">Nothing recent.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((p) => {
            const meta = PROJECT_STAGES_BY_KEY[p.stage] || {};
            return (
              <li key={p.id} className="py-2.5">
                <Link to={`/projects/${p.id}`} className="flex items-center justify-between gap-3 hover:text-orange-600">
                  <div className="min-w-0">
                    <div className="font-bold text-sm truncate">{p.name}</div>
                    <div className="text-xs text-slate-500 truncate font-mono">{(p.project_id || "").replace(/^HK\s*-\s*/i, "")}</div>
                  </div>
                  <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full whitespace-nowrap shrink-0"
                    style={{ color: meta.color, backgroundColor: meta.bg }}>{meta.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function MyPendingCard({ mine, projectOnly }) {
  const stripHK = (pid) => (pid || "").replace(/^HK\s*-\s*/i, "");
  return (
    <section className="hk-card p-5 mb-6 border-l-4" style={{ borderLeftColor: "#E52B50" }} data-testid="my-pending-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="hk-tiny-label" style={{ color: "#E52B50" }}>Waiting on you</div>
          <h2 className="text-xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>
            {mine.total} item{mine.total === 1 ? "" : "s"} pending your action
          </h2>
        </div>
      </div>
      <div className={`grid gap-4 ${projectOnly ? "md:grid-cols-1" : "md:grid-cols-2"}`}>
        {!projectOnly && (
        <div>
          <div className="hk-tiny-label mb-2">Enquiries ({mine.enquiries.length})</div>
          {mine.enquiries.length === 0 ? (
            <div className="text-xs text-slate-400" data-testid="my-pending-enq-empty">Nothing pending</div>
          ) : (
            <ul className="space-y-1.5">
              {mine.enquiries.slice(0, 8).map((e) => {
                const meta = ENQUIRY_STAGES_BY_KEY[e.stage] || {};
                return (
                  <li key={e.id} data-testid={`my-pending-enq-${e.id}`}>
                    <Link to={`/enquiries/${e.id}`} className="flex items-center justify-between gap-2 border border-slate-200 hover:border-slate-400 rounded-sm px-2 py-1.5 text-xs">
                      <div className="min-w-0">
                        <div className="font-bold text-slate-900 truncate">{e.name}</div>
                        <div className="text-slate-500 truncate">{e.phone}</div>
                      </div>
                      <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-full whitespace-nowrap shrink-0"
                        style={{ color: meta.color, backgroundColor: meta.bg }}>{meta.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        )}
        <div>
          <div className="hk-tiny-label mb-2">Projects ({mine.projects.length})</div>
          {mine.projects.length === 0 ? (
            <div className="text-xs text-slate-400" data-testid="my-pending-proj-empty">Nothing pending</div>
          ) : (
            <ul className="space-y-1.5">
              {mine.projects.slice(0, 8).map((p) => {
                const meta = PROJECT_STAGES_BY_KEY[p.stage] || {};
                return (
                  <li key={p.id} data-testid={`my-pending-proj-${p.id}`}>
                    <Link to={`/projects/${p.id}`} className="flex items-center justify-between gap-2 border border-slate-200 hover:border-slate-400 rounded-sm px-2 py-1.5 text-xs">
                      <div className="min-w-0">
                        <div className="font-bold text-slate-900 truncate">{p.customer_name} - {p.name}</div>
                        <div className="text-slate-500 truncate font-mono">{stripHK(p.project_id)}</div>
                      </div>
                      <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-full whitespace-nowrap shrink-0"
                        style={{ color: meta.color, backgroundColor: meta.bg }}>{meta.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}


/** Minimal task-queue dashboard for the 2D/3D Design role.
 * No KPIs, no pipelines, no status panels — just a list of enquiries currently
 * waiting on drawings, each with a Ð "Open & upload" button. */
function DesignTaskQueue({ user, mine, data }) {
  const [drawingList, setDrawingList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        // No stage filter — the backend's Design scope already returns both
        // `drawing`-stage enquiries and `converted`-stage enquiries with an
        // open design_brief (iter69 re-requests from direct-quote projects).
        const { data: rows } = await api.get("/enquiry");
        setDrawingList(Array.isArray(rows) ? rows : []);
      } catch (e) { /* noop */ }
      finally { setLoading(false); }
    })();
  }, []);
  return (
    <DesktopLayout>
      <div className="mb-6">
        <div className="hk-tiny-label" style={{ color: "#E52B50" }}>2D / 3D Design</div>
        <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>
          Hey {user?.name?.split(" ")[0] || "Designer"},
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Projects assigned to you · <span className="font-semibold">{drawingList.length} pending</span>
        </p>
      </div>

      <section className="hk-card p-5" data-testid="design-task-queue">
        <div className="hk-tiny-label mb-4 flex items-center gap-1">
          <Upload className="w-3 h-3" /> Projects assigned to 2D/3D
        </div>
        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : drawingList.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-sm" data-testid="design-empty">
            No projects pending. Great work!
          </div>
        ) : (
          <ul className="space-y-2">
            {drawingList.map((e) => (
              <li key={e.id} data-testid={`design-queue-${e.id}`}>
                <Link to={`/design/${e.id}`}
                      className="flex items-center justify-between gap-3 border border-slate-200 hover:border-orange-500 rounded-sm px-4 py-3 group">
                  <div className="min-w-0 flex-1">
                    <div className="font-black text-slate-900 tracking-tight truncate">{e.name}</div>
                    <div className="text-xs text-slate-500 truncate">Ref: {e.enquiry_id}</div>
                    {e.design_brief?.sent_by_name && (
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        Sent by: <b className="text-slate-700">{e.design_brief.sent_by_name}</b>
                        {e.design_brief.notes && <span className="ml-1">· {e.design_brief.notes.slice(0, 60)}{e.design_brief.notes.length > 60 ? "…" : ""}</span>}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    <span className="hk-tiny-label text-orange-700">Open</span>
                    <ArrowRight className="w-4 h-4 text-orange-600 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </DesktopLayout>
  );
}


/** Pending Tasks — the "waiting on me" table shown on every user's dashboard.
 * Columns exactly per user spec: SlNo · Customer · Project name · Sales Person
 * · Assigned By · Assigned On · Days Passed · Status. */
function PendingTasksTable({ rows }) {
  if (!rows || rows.length === 0) return null;
  const stripHK = (pid) => (pid || "").replace(/^HK\s*-\s*/i, "");
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  }) : "—";
  return (
    <section className="hk-card p-5 mb-6" data-testid="pending-tasks-table">
      <h2 className="hk-tiny-label mb-3 flex items-center gap-1">
        <Clock className="w-3 h-3" /> Pending tasks · {rows.length}
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] font-black uppercase text-slate-500 border-b border-slate-200">
              <th className="text-left py-2 px-2 w-10">Sl No</th>
              <th className="text-left py-2 px-2">Customer</th>
              <th className="text-left py-2 px-2">Project Name</th>
              <th className="text-left py-2 px-2">Sales Person</th>
              <th className="text-left py-2 px-2">Assigned By</th>
              <th className="text-left py-2 px-2">Assigned On</th>
              <th className="text-right py-2 px-2">Days</th>
              <th className="text-left py-2 px-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const daysColor = r.days_passed == null ? "text-slate-500"
                : r.days_passed >= 7 ? "text-red-600 font-black"
                : r.days_passed >= 3 ? "text-amber-700 font-bold"
                : "text-slate-700";
              return (
                <tr key={r.id} data-testid={`pending-row-${r.id}`}
                    className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 px-2 text-slate-500">{idx + 1}</td>
                  <td className="py-2 px-2">
                    <Link to={`/projects/${r.id}`} className="font-bold text-slate-900 hover:text-orange-700">
                      {r.customer_name || "—"}
                    </Link>
                  </td>
                  <td className="py-2 px-2 text-slate-700 truncate max-w-[220px]">
                    {r.name || "—"}
                    <div className="text-[10px] text-slate-400 font-mono">{stripHK(r.project_id)}</div>
                  </td>
                  <td className="py-2 px-2 text-slate-700">{r.sales_person_name || "—"}</td>
                  <td className="py-2 px-2 text-slate-700">{r.assigned_by_name || "—"}</td>
                  <td className="py-2 px-2 text-slate-700 whitespace-nowrap">{fmtDate(r.assigned_on)}</td>
                  <td className={`py-2 px-2 text-right ${daysColor}`}>{r.days_passed ?? "—"}</td>
                  <td className="py-2 px-2 min-w-[220px]">
                    {!r.production_progress && (
                      <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-sm bg-orange-50 text-orange-800">
                        {r.workflow_status || r.stage}
                      </span>
                    )}
                    <StationProgress prog={r.production_progress} id={r.id} compact />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}


/** Production / Installation restructured dashboard.
 * - Pending: projects at their active workflow stage (sorted by delivery)
 * - Upcoming: projects at the immediately previous stage
 * Click a row → ProjectDetail (which handles the shared redacted view). */
function ProductionBoardDashboard({ user }) {
  const [board, setBoard] = React.useState({ pending: [], upcoming: [], role: user?.role });
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/project/production-board");
        setBoard(data);
      } catch (_) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);
  const roleLabel = user?.role === "installation" ? "Installation" : "Production";
  return (
    <DesktopLayout>
      <div className="max-w-[520px] mx-auto" data-testid="production-mobile-view">
      <div className="mb-6">
        <div className="hk-tiny-label" style={{ color: "#E52B50" }}>{roleLabel} board</div>
        <h1 className="text-3xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>
          Hey {user?.name?.split(" ")[0] || "there"},
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Pending on you, plus what's coming up next.
        </p>
      </div>
      {loading ? (
        <div className="hk-card p-8 text-center text-slate-500">Loading board…</div>
      ) : (
        <>
          <BoardSectionMobile title="Pending on you" rows={board.pending} accent="rose" showQueue />
          <BoardSectionMobile title="Upcoming" rows={board.upcoming} accent="slate"
            emptyLabel={user?.role === "installation"
              ? "No projects currently in Production."
              : "No projects currently in Design Drawings."} />
        </>
      )}
      </div>
    </DesktopLayout>
  );
}

/** Mobile-first card list for Production + Installation boards. Each row is a
 * chunky tap target with the four key fields stacked. */
function BoardSectionMobile({ title, rows, accent = "slate", emptyLabel, showQueue = false }) {
  const dot = accent === "rose" ? "bg-rose-500" : "bg-slate-400";
  return (
    <section className="mb-6" data-testid={`board-${title.toLowerCase().replace(/\s+/g,'-')}`}>
      <h2 className="hk-tiny-label mb-2 flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${dot}`}></span>
        {title} · {rows.length}
      </h2>
      {rows.length === 0 ? (
        <div className="hk-card p-5 text-center text-sm text-slate-500">
          {emptyLabel || "Nothing here yet."}
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((p) => {
            const dd = p.estimated_delivery_date;
            const daysLeft = dd ? Math.ceil((new Date(dd) - new Date()) / 86400000) : null;
            const overdue = daysLeft !== null && daysLeft < 0 && p.stage !== "completed";
            return (
              <li key={p.id} className="hk-card p-3 active:bg-slate-50 cursor-pointer"
                onClick={() => window.location.assign(`/projects/${p.id}`)}
                data-testid={`board-row-${p.id}`}>
                <div className="flex items-start gap-2">
                  {showQueue && (
                    <div className="w-10 h-10 rounded-sm bg-rose-100 border border-rose-200 text-rose-800 font-black flex items-center justify-center text-sm shrink-0">
                      #{p.queue_position || "—"}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 text-base leading-tight truncate" data-testid={`board-customer-${p.id}`}>
                      {p.customer_name || "—"}
                    </div>
                    <div className="text-sm font-semibold text-slate-600 truncate" data-testid={`board-project-${p.id}`}>
                      {p.name}
                    </div>
                    <div className="text-[11px] text-slate-400 truncate">
                      {(p.project_id || "").replace(/^HK\s*-\s*/i, "")}
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                      <div><span className="text-slate-500">Delivery:</span> <b className="text-slate-800">{dd || "—"}</b></div>
                      <div><span className="text-slate-500">Days left:</span>{" "}
                        <b className={overdue ? "text-red-700" : daysLeft !== null && daysLeft <= 3 ? "text-amber-700" : "text-slate-800"}>
                          {daysLeft === null ? "—" : overdue ? `${Math.abs(daysLeft)}d overdue` : `${daysLeft}d`}
                        </b>
                      </div>
                      <div className="col-span-2 truncate">
                        <span className="text-slate-500">Sales:</span> <b className="text-slate-800">{p.assigned_sales_name || "—"}</b>
                      </div>
                    </div>
                    {!p.production_progress && (
                      <div className="mt-2 text-[10px] font-black uppercase text-slate-700 bg-slate-100 border border-slate-200 rounded-sm px-2 py-1 inline-block truncate max-w-full">
                        {p.workflow_status || p.stage?.replace(/_/g, " ")}
                      </div>
                    )}
                    <StationProgress prog={p.production_progress} id={p.id} />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function StationProgress({ prog, id, compact = false }) {
  if (!prog || !prog.total) return null;
  const { done, total, current_name: name, current_status: status, current_index: idx } = prog;
  const label = {
    in_progress: "In Progress", started: "In Progress", in_queue: "In Queue",
    pending: "Not started", on_hold: "On Hold", info_required: "Info Required",
    completed: "Completed",
  }[status] || "Not started";
  const tone = {
    in_progress: "text-emerald-700 bg-emerald-50 border-emerald-200",
    started: "text-emerald-700 bg-emerald-50 border-emerald-200",
    on_hold: "text-red-700 bg-red-50 border-red-200",
    info_required: "text-amber-700 bg-amber-50 border-amber-200",
    in_queue: "text-blue-700 bg-blue-50 border-blue-200",
  }[status] || "text-slate-600 bg-slate-50 border-slate-200";
  const pct = Math.round((done / total) * 100);
  return (
    <div className={compact ? "mt-1" : "mt-2"} data-testid={`station-progress-${id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold text-slate-900 truncate">
          {name ? name : "All stations complete"}
        </span>
        <span className={`text-[10px] font-black uppercase border px-1.5 py-0.5 rounded-sm ${tone}`}>
          {name ? label : "Done"}
        </span>
        <span className="text-[11px] text-slate-500 whitespace-nowrap">
          {name ? `Station ${idx} of ${total}` : `${total} stations`} · {done}/{total} done
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-slate-800 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      {prog.hold_reason && (
        <div className="text-[11px] text-red-700 mt-1 truncate">{prog.hold_reason}</div>
      )}
    </div>
  );
}

function BoardSection({ title, rows, accent = "slate", emptyLabel, showQueue = false }) {
  const dot = accent === "rose" ? "bg-rose-500" : "bg-slate-400";
  return (
    <section className="mb-8" data-testid={`board-${title.toLowerCase().replace(/\s+/g,'-')}`}>
      <h2 className="hk-tiny-label mb-2 flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${dot}`}></span>
        {title} · {rows.length}
      </h2>
      {rows.length === 0 ? (
        <div className="hk-card p-6 text-center text-sm text-slate-500">
          {emptyLabel || "Nothing here yet."}
        </div>
      ) : (
        <div className="hk-card overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="bg-slate-50 hk-tiny-label">
                {showQueue && <th className="px-4 py-3 text-left w-14">#</th>}
                <th className="px-4 py-3 text-left">Customer / Project</th>
                <th className="px-4 py-3 text-left">Delivery</th>
                <th className="px-4 py-3 text-left">Days left</th>
                <th className="px-4 py-3 text-left">Sales Person</th>
                <th className="px-4 py-3 text-left">Production Station</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const dd = p.estimated_delivery_date;
                const daysLeft = dd ? Math.ceil((new Date(dd) - new Date()) / 86400000) : null;
                const overdue = daysLeft !== null && daysLeft < 0 && p.stage !== "completed";
                return (
                  <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => window.location.assign(`/projects/${p.id}`)}
                    data-testid={`board-row-${p.id}`}>
                    {showQueue && (
                      <td className="px-4 py-3 text-xs font-black text-slate-500">
                        {p.queue_position || "—"}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="font-bold text-slate-900" data-testid={`board-customer-${p.id}`}>{p.customer_name || "—"}</div>
                      <div className="text-sm font-semibold text-slate-600" data-testid={`board-project-${p.id}`}>{p.name}</div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {(p.project_id || "").replace(/^HK\s*-\s*/i, "")}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap text-slate-700">
                      {dd || "—"}
                    </td>
                    <td className={`px-4 py-3 text-xs whitespace-nowrap ${overdue ? "text-red-700 font-bold" : daysLeft !== null && daysLeft <= 3 ? "text-amber-700 font-semibold" : "text-slate-700"}`}>
                      {daysLeft === null ? "—" : overdue ? `${Math.abs(daysLeft)}d overdue` : `${daysLeft}d`}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">
                      {p.assigned_sales_name || "—"}
                    </td>
                    <td className="px-4 py-3 text-xs min-w-[260px]">
                      {p.production_progress
                        ? <StationProgress prog={p.production_progress} id={p.id} compact />
                        : (p.workflow_status || p.stage?.replace(/_/g, " "))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}



